import React, { useState, useEffect, useContext } from "react";
import { Route, Redirect } from "react-router-dom";
import { getStoredRole } from "./authority";
import { AuthContext } from "./Contexts/AuthContext";

// Protected route component that checks if the user is authorized before allowing access
const ProtectedRoute = ({ component: Component, userTypes = [], ...rest }) => {
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser, loading, authChecked } = useContext(AuthContext);

  useEffect(() => {
    const checkAuth = async () => {
      // Wait for the initial auth check to complete
      if (!authChecked) {
        return;
      }

      // Check for a guest session first. The id is stored as "sessionId" (and
      // mirrored into a cookie); the old key "session" was never written by anything,
      // so this branch never fired and signed-out visitors were always bounced to
      // the login page instead of being allowed to read the tense map, take quizzes
      // and keep notes.
      const guestSession =
        localStorage.getItem("sessionId") || localStorage.getItem("session");

      // No signed-in user: a guest may use the learning features — the routes that
      // name no roles, like the notepad — but not the ones reserved for accounts,
      // like multiplayer. This branch used to authorise every protected route for
      // anyone with a session, so a visitor could walk into a signed-in-only page.
      if (!currentUser) {
        const guestsAllowed = userTypes.length === 0 || userTypes.includes(4);
        setIsAuthorized(Boolean(guestSession) && guestsAllowed);
        setIsLoading(false);
        return;
      }

      // If user is logged in, proceed with role-based authorization
      try {
        const userRole = currentUser.role || getStoredRole();

        // Check if role is a number
        let normalizedRole;
        if (typeof userRole === "number") {
          normalizedRole = userRole;
        } else if (typeof userRole === "string" && /^\d+$/.test(userRole)) {
          normalizedRole = parseInt(userRole, 10);
        } else {
          normalizedRole = userRole; // Role is a string like "admin"
        }

        // Authorize if no specific userTypes are required or if the user's role matches
        if (userTypes.length === 0 || userTypes.includes(normalizedRole)) {
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error("Authorization check failed:", error);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [currentUser, authChecked, userTypes]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
        <p className="ml-2">Loading...</p>
      </div>
    );
  }

  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthorized ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              // A visitor sent to sign in, so they can come back to where they were
              // going; a signed-in user who lacks the role goes home instead of into
              // a login loop.
              pathname: localStorage.getItem("jstoken") ? "/" : "/login_register",
              state: {
                from: props.location,
              },
            }}
          />
        )
      }
    />
  );
};

export default ProtectedRoute;
